import { FC, useCallback, useContext, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import {
  Alert,
  Button,
  Chip,
  Rating,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { Autorenew } from '@mui/icons-material';

import { useAnalysisCrud } from '@modules/Analysis/hooks/useAnalysisCrud';
import { FeedbackContext } from '@cvt/contexts';


interface ContextualAnalysisPanelProps {
  storyThrough: Stories.StoryThrough;
  fieldPath: string;
}

const scoreColor = (score25: number) => {
  if (score25 >= 22) return 'success';
  if (score25 >= 18) return 'warning';
  return 'error';
};

export const ContextualAnalysisPanel: FC<ContextualAnalysisPanelProps> = (props) => {
  const { storyThrough, fieldPath } = props;
  const { createContextualAnalysis } = useAnalysisCrud();
  const { genericErrorFeedback } = useContext(FeedbackContext);
  const { control, getValues, setValue } = useFormContext();

  const {
    momentOfReflection: momentOfReflectionValue,
    meetingAdaptation: meetingAdaptationValue,
    contextualFeedback: feedback,
  } = useWatch({
    control,
    name: fieldPath,
  }) as Stories.StoryThrough;

  const [lastAnalyzedMoment, setLastAnalyzedMoment] = useState<string | undefined>(
    storyThrough.momentOfReflection ?? undefined,
  );

  const canRun = useMemo(() => {
    return Boolean(momentOfReflectionValue && momentOfReflectionValue.trim().length > 0);
  }, [momentOfReflectionValue]);

  const dirty = useMemo(() => {
    return Boolean(feedback && lastAnalyzedMoment && momentOfReflectionValue !== lastAnalyzedMoment);
  }, [feedback, lastAnalyzedMoment, momentOfReflectionValue]);

  const runAnalysis = useCallback(async () => {
    const current = getValues(fieldPath) as {
      meetingAdaptation?: string;
      momentOfReflection?: string;
    };

    const payload = {
      id: storyThrough.id,
      meetingAdaptation:
        current?.meetingAdaptation ?? meetingAdaptationValue ?? storyThrough.meetingAdaptation ?? '',
      momentOfReflection:
        current?.momentOfReflection ?? momentOfReflectionValue ?? storyThrough.momentOfReflection ?? '',
    };

    try {
      const normalized = await createContextualAnalysis.mutateAsync(payload as any);
      setValue(`${fieldPath}.contextualFeedback`, normalized);
      setLastAnalyzedMoment(payload.momentOfReflection);
    } catch {
      genericErrorFeedback();
    }
  }, [getValues, fieldPath, storyThrough.id, storyThrough.meetingAdaptation, storyThrough.momentOfReflection, meetingAdaptationValue, momentOfReflectionValue, createContextualAnalysis, setValue, genericErrorFeedback]);

  const [tab, setTab] = useState(0);
  const { isPending: isLoading, isError, error } = createContextualAnalysis;
  const errorMessage = (error as any)?.message;
  const hasFeedback = !!feedback;

  const score25 = Number(feedback?.scoreContextual ?? 0);
  const strengths = feedback?.strengths ?? [];
  const adjustments = feedback?.adjustments ?? [];
  const breakdown = feedback?.breakdown ?? [];

  return (
    <Stack gap={2}>
      {hasFeedback && dirty && (
        <Alert
          severity="warning"
          variant="outlined"
          sx={{ alignItems: 'center' }}
          action={
            <Button
              size="small"
              color="warning"
              onClick={runAnalysis}
              startIcon={
                <Autorenew
                  fontSize="small"
                  sx={{
                    animation: isLoading ? 'spin 1s linear infinite' : 'none',
                    '@keyframes spin': {
                      from: { transform: 'rotate(0deg)' },
                      to: { transform: 'rotate(360deg)' },
                    },
                  }}
                />
              }
              disabled={isLoading}
            >
              Re-run
            </Button>
          }
        >
          Meeting details changed since last analysis. Re-run to refresh feedback.
        </Alert>
      )}

      {!hasFeedback && (
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Stack>
            <Typography variant="body2" color="text.secondary">
              No analysis yet.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Run the analysis to see how well this story fits this meeting context.
            </Typography>
          </Stack>
          <Button
            variant="contained"
            color="primary"
            onClick={runAnalysis}
            disabled={!canRun || isLoading}
            startIcon={
              <Autorenew
                sx={{
                  animation: isLoading ? 'spin 1s linear infinite' : 'none',
                  '@keyframes spin': {
                    from: { transform: 'rotate(0deg)' },
                    to: { transform: 'rotate(360deg)' },
                  },
                }}
              />
            }
          >
            Run Analysis
          </Button>
        </Stack>
      )}

      {isLoading && (
        <Stack gap={2}>
          <Typography variant="body2" color="text.secondary">
            Analyzing meeting fit…
          </Typography>
          <Tabs value={tab} onChange={(_, v) => setTab(v)}>
            <Tab label="Fit Summary" disabled/>
            <Tab label="Detailed Scoring" disabled/>
          </Tabs>
          <Stack gap={1}>
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} height={20}/>
            ))}
          </Stack>
        </Stack>
      )}

      {isError && (
        <Stack gap={1}>
          <Alert severity="error">
            <Typography variant="body2" fontWeight={600}>
              Couldn't analyze fit right now.
            </Typography>
            <Typography variant="body2">Please try again in a moment.</Typography>
          </Alert>
          <Button
            variant="outlined"
            onClick={runAnalysis}
            startIcon={
              <Autorenew
                sx={{
                  animation: isLoading ? 'spin 1s linear infinite' : 'none',
                  '@keyframes spin': {
                    from: { transform: 'rotate(0deg)' },
                    to: { transform: 'rotate(360deg)' },
                  },
                }}
              />
            }
            disabled={isLoading}
          >
            Retry
          </Button>
          {errorMessage && (
            <Typography variant="caption" color="text.secondary">
              {errorMessage}
            </Typography>
          )}
        </Stack>
      )}

      {hasFeedback && !isLoading && !isError && (
        <Stack gap={2}>
          <Tabs value={tab} onChange={(_, value) => setTab(value)} centered>
            <Tab label="Fit Summary"/>
            <Tab label="Detailed Scoring"/>
          </Tabs>

          {tab === 0 && (
            <Stack gap={2} mt={1}>
              <Stack
                direction="row"
                flexWrap="wrap"
                gap={1.5}
                alignItems="center"
                sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}
              >
                <Typography variant="subtitle2">
                  Contextual Score
                </Typography>
                <Chip color={scoreColor(score25) as any} label={`${score25} / 25`} sx={{ fontWeight: 700 }}/>
                <Chip variant="outlined" label={feedback?.tier ?? '—'}/>
              </Stack>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontStyle: 'italic', borderLeft: 3, borderColor: 'primary.main', pl: 2 }}
              >
                {feedback?.fitSummary || 'Analysis summary unavailable.'}
              </Typography>
              <Stack direction={{ xs: 'column', md: 'row' }} gap={3}>
                <Stack flex={1}>
                  <Typography variant="subtitle2" color="success.main">
                    Strengths
                  </Typography>
                  {strengths.length > 0 ? (
                    <Stack component="ul" sx={{ pl: 2, m: 0 }} gap={1}>
                      {strengths.map((s, i) => (
                        <Typography key={i} component="li" variant="body2">
                          {s}
                        </Typography>
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No strengths identified.
                    </Typography>
                  )}
                </Stack>

                <Stack flex={1}>
                  <Typography variant="subtitle2" color="warning.main">
                    Adjustments
                  </Typography>
                  {adjustments.length > 0 ? (
                    <Stack component="ul" sx={{ pl: 2, m: 0 }} gap={1}>
                      {adjustments.map((a, i) => (
                        <Typography key={i} component="li" variant="body2">
                          {a}
                        </Typography>
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No adjustments provided.
                    </Typography>
                  )}
                </Stack>
              </Stack>
            </Stack>
          )}

          {tab === 1 && (
            <Stack gap={1} mt={1}>
              {breakdown.length > 0 ? (
                breakdown.map((item, idx) => (
                  <Stack
                    key={`${item.dimension}-${idx}`}
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    py={1}
                    borderBottom={idx === breakdown.length - 1 ? 'none' : '1px solid'}
                    borderColor="divider"
                  >
                    <Stack flex={1} minWidth={0}>
                      <Stack direction="row" alignItems="center" gap={1} mb={0.5}>
                        <Typography variant="body2" fontWeight={500}>
                          {item.dimension}
                        </Typography>
                        <Rating size="small" value={Number(item.score) || 0} max={5} readOnly/>
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        {item.rationale || 'No rationale provided.'}
                      </Typography>
                    </Stack>
                  </Stack>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No detailed scoring available.
                </Typography>
              )}

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}
              >
                Analysis completed: {feedback?.modified ? new Date(feedback.modified).toLocaleString() : '—'}
              </Typography>
            </Stack>
          )}
        </Stack>
      )}

      {(isLoading || hasFeedback) && !isError && (
        <Stack pt={1} borderTop="1px solid" borderColor="divider">
          <Button
            variant="outlined"
            onClick={runAnalysis}
            startIcon={
              <Autorenew
                sx={{
                  animation: isLoading ? 'spin 1s linear infinite' : 'none',
                  '@keyframes spin': {
                    from: { transform: 'rotate(0deg)' },
                    to: { transform: 'rotate(360deg)' },
                  },
                }}
              />
            }
            disabled={isLoading || !canRun}
          >
            Refresh Analysis
          </Button>
        </Stack>
      )}
    </Stack>
  );
};